import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { ClientApi, ProjectApi, GroupApi } from "../../../apis";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  name: Yup.string().required("Project name is required"),
  description: Yup.string().required("Description is required"),
  status: Yup.string().required("Status is required"),
  clientId: Yup.string().required("Client is required"),
  invoiceDetails: Yup.string(),
  phases: Yup.array()
    .of(
      Yup.object({
        phaseName: Yup.string().required("Phase name is required"),
        amount: Yup.number()
          .typeError("Amount must be a number")
          .min(0, "Amount must be positive")
          .required("Amount is required"),
        percentComplete: Yup.number()
          .typeError("Percent must be a number")
          .min(0, "Minimum 0%")
          .max(100, "Maximum 100%")
          .required("Percent complete is required"),
      })
    )
    .min(1, "At least one phase is required"),
});

const createPhase = () => ({ phaseName: "", amount: "", percentComplete: "" });
const baseInitialValues = {
  name: "",
  description: "",
  status: "active",
  clientId: "",
  invoiceDetails: "",
  phases: [createPhase()],
  groupIds: [],
};

const normalizePhases = (phases) => {
  if (!Array.isArray(phases) || phases.length === 0) {
    return [createPhase()];
  }
  return phases.map((phase) => ({
    phaseName: phase.phaseName || phase.name || "",
    amount:
      phase.amount !== undefined && phase.amount !== null
        ? phase.amount.toString()
        : "",
    percentComplete:
      phase.percentComplete !== undefined && phase.percentComplete !== null
        ? phase.percentComplete.toString()
        : phase.percent_complete !== undefined && phase.percent_complete !== null
        ? phase.percent_complete.toString()
        : "",
  }));
};

function AddProject() {
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(baseInitialValues);
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isEditing = Boolean(projectId);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          name: values.name,
          description: values.description,
          status: values.status,
          clientId: values.clientId,
          invoiceDetails: values.invoiceDetails || "",
          phases: values.phases.map((phase) => ({
            name: phase.phaseName,
            amount: Number(phase.amount) || 0,
            percentComplete: Number(phase.percentComplete) || 0,
          })),
          contractAmount: totalContractAmount,
          groupIds: values.groupIds,
        };

        if (isEditing) {
          await ProjectApi.updateProject(projectId, payload);
          toast.success("Project updated successfully");
        } else {
          await ProjectApi.createProject(payload);
          toast.success("Project created successfully");
        }
        navigate("/admin/projects");
      } catch (error) {
        console.error("Error saving project:", error);
        toast.error(
          error.response?.data?.message || "Failed to save project"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const totalContractAmount = useMemo(() => {
    return formik.values.phases.reduce((sum, phase) => {
      const amount = parseFloat(phase.amount);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
  }, [formik.values.phases]);

  const fetchClients = async () => {
    try {
      const res = await ClientApi.getAllClients();
      const data = res?.data?.data;
      const clientList = Array.isArray(data?.clients)
        ? data.clients
        : Array.isArray(data)
        ? data
        : [];
      setClients(clientList);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClients([]);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await GroupApi.getAllGroups();
        const data = res?.data?.data;
        const groupList = Array.isArray(data)
          ? data
          : Array.isArray(data?.groups)
          ? data.groups
          : [];
        setGroups(
          groupList.map((group) => ({
            id: group.id,
            name: group.name,
          }))
        );
      } catch (error) {
        console.error("Error fetching groups:", error);
        setGroups([]);
      }
    };

    fetchGroups();
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    const fetchProject = async () => {
      setIsProjectLoading(true);
      try {
        const res = await ProjectApi.getProjectById(projectId);
        const project = res?.data?.data?.project || res?.data?.data || {};

        setInitialValues({
          name: project.name || "",
          description: project.description || "",
          status: project.status || "active",
          clientId: (project.clientId || project.client?.id || "").toString(),
          invoiceDetails: project.invoiceDetails || "",
          phases: normalizePhases(project.phases),
          groupIds: Array.isArray(project.groups)
            ? project.groups.map((group) => group.id)
            : [],
        });
      } catch (error) {
        console.error("Error fetching project:", error);
        toast.error("Failed to load project details");
      } finally {
        setIsProjectLoading(false);
      }
    };

    fetchProject();
  }, [isEditing, projectId]);

  const handleAddPhase = () => {
    formik.setFieldValue("phases", [...formik.values.phases, createPhase()]);
  };

  const handleRemovePhase = (index) => {
    const nextPhases = formik.values.phases.filter((_, idx) => idx !== index);
    formik.setFieldValue(
      "phases",
      nextPhases.length ? nextPhases : [createPhase()]
    );
  };

  const pageTitle = isEditing ? "Edit Project" : "Add Project";
  const submitLabel = isEditing ? "Update" : "Submit";
  const isSubmitDisabled = loading || (isEditing && isProjectLoading);

  if (isEditing && isProjectLoading) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} gutterBottom mb={2}>
          {pageTitle}
        </Typography>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mx: { xs: -2, md: 0 }, borderRadius: { xs: 0, md: 1 } }}>
          <Typography variant="body1">Loading project details...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom mb={2}>
        {pageTitle}
      </Typography>

      <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mx: { xs: -2, md: 0 }, borderRadius: { xs: 0, md: 1 } }}>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            "& .MuiTextField-root": { mb: 2 },
          }}
        >
          {/* columnSpacing applies a negative left margin (-8px per unit). At the mobile Paper padding that exceeded the gutter and pushed the grid past the left edge, so the page looked off-centre. Items are xs={12} on a phone anyway, so there is no column gap to keep there. */}
          <Grid container rowSpacing={3} columnSpacing={{ xs: 0, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Project Name *
              </Typography>
              <TextField
                fullWidth
                id="name"
                name="name"
                placeholder="Enter project name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Status *
              </Typography>
              <TextField
                select
                fullWidth
                id="status"
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.status && Boolean(formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Description *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                id="description"
                name="description"
                placeholder="Enter project description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Required Project Details for Invoices
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                id="invoiceDetails"
                name="invoiceDetails"
                placeholder="Enter project details that should appear on invoices"
                value={formik.values.invoiceDetails}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.invoiceDetails &&
                  Boolean(formik.errors.invoiceDetails)
                }
                helperText={
                  formik.touched.invoiceDetails && formik.errors.invoiceDetails
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Client *
              </Typography>
              <TextField
                select
                fullWidth
                id="clientId"
                name="clientId"
                placeholder="Select client"
                value={formik.values.clientId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.clientId && Boolean(formik.errors.clientId)}
                helperText={formik.touched.clientId && formik.errors.clientId}
              >
                <MenuItem value="" disabled>
                  Select client
                </MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.clientNickname || client.company || `Client #${client.id}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Assign Groups
              </Typography>
              <Autocomplete
                multiple
                options={groups}
                getOptionLabel={(option) => option.name || ""}
                value={groups.filter((group) =>
                  formik.values.groupIds.includes(group.id)
                )}
                onChange={(_, newValue) =>
                  formik.setFieldValue(
                    "groupIds",
                    newValue.map((group) => group.id)
                  )
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select group(s)"
                    fullWidth
                    margin="normal"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Total Contract Amount
              </Typography>
              <TextField
                fullWidth
                value={`$${totalContractAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Phases
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleIcon />}
                  onClick={handleAddPhase}
                >
                  Add Phase
                </Button>
              </Box>

              {formik.values.phases.map((phase, index) => (
                <Paper
                  key={`phase-${index}`}
                  variant="outlined"
                  sx={{ p: { xs: 2, md: 3 }, mb: 2, borderColor: "#E0E7FF" }}
                >
                  <Grid container rowSpacing={2} columnSpacing={{ xs: 0, md: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Phase Name *
                      </Typography>
                      <TextField
                        fullWidth
                        name={`phases[${index}].phaseName`}
                        placeholder="Enter phase name"
                        value={phase.phaseName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.phases?.[index]?.phaseName &&
                          Boolean(formik.errors?.phases?.[index]?.phaseName)
                        }
                        helperText={
                          formik.touched?.phases?.[index]?.phaseName &&
                          formik.errors?.phases?.[index]?.phaseName
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Amount *
                      </Typography>
                      <TextField
                        fullWidth
                        name={`phases[${index}].amount`}
                        placeholder="Enter amount"
                        value={phase.amount}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.phases?.[index]?.amount &&
                          Boolean(formik.errors?.phases?.[index]?.amount)
                        }
                        helperText={
                          formik.touched?.phases?.[index]?.amount &&
                          formik.errors?.phases?.[index]?.amount
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" gutterBottom>
                        % Complete *
                      </Typography>
                      <TextField
                        fullWidth
                        name={`phases[${index}].percentComplete`}
                        placeholder="0 - 100"
                        value={phase.percentComplete}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.phases?.[index]?.percentComplete &&
                          Boolean(formik.errors?.phases?.[index]?.percentComplete)
                        }
                        helperText={
                          formik.touched?.phases?.[index]?.percentComplete &&
                          formik.errors?.phases?.[index]?.percentComplete
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={1}
                      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                    >
                      <IconButton
                        color="error"
                        onClick={() => handleRemovePhase(index)}
                        aria-label="Remove phase"
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              {typeof formik.errors.phases === "string" && (
                <Typography color="error" variant="caption">
                  {formik.errors.phases}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/admin/projects")}
                  disabled={isSubmitDisabled}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitDisabled}
                  sx={{ minWidth: 140 }}
                >
                  {loading ? "Submitting..." : submitLabel}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default AddProject;

